import { Component, Input, OnChanges, OnInit, OnDestroy, Output, EventEmitter, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { MatSidenav } from '@angular/material';

import { filter } from 'rxjs/operators/filter';
import { map } from 'rxjs/operators/map';
import { Subscription } from 'rxjs/Subscription';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators/takeUntil';

import { UserInfo } from '../../../services/user/user.service';

@Component({
    selector: 'app-nav-menu',
    styleUrls: ['navmenu.component.css'],
    templateUrl: 'navmenu.component.html',
})
export class NavMenuComponent implements OnChanges, OnInit, OnDestroy {
    @ViewChild('navmenu') navmenu: ElementRef;

    private _subRelease: Subject<void> = new Subject<void>();
    private _routerSub: Subscription;

    @Input() items: NavMenuItem[];
    @Input() logout: Function;
    @Input() about: Function;
    @Input() user: UserInfo;

    @Output() navigate: EventEmitter<string> = new EventEmitter<string>();

    FilteredItems: NavMenuItem[];

    constructor(private router: Router, private route: ActivatedRoute) {}

    // #region Clear All Active Buttons Method

    ClearAllActiveButtons() {
        // Clear all active flags
        if (this.FilteredItems && this.FilteredItems.length > 0) {
            this.FilteredItems.forEach(item => {
                item.Active = false;
            });
        }
    }

    // #endregion

    HandleItemClick(item: NavMenuItem) {
        if (item == null) {
            return;
        }

        if (item.IsLogout === true || item.IsAbout === true) {
            // Get a handle to the currently active nav item.
            const curActive = this.FilteredItems.find(i => {
                return i.Active;
            });

            // Clear all actives
            this.ClearAllActiveButtons();

            // Set the About button as active visually.
            item.Active = true;

            // Call the appropriate method, and wait for the dialog to dismiss.
            const methodFn = item.IsLogout ? this.logout : this.about;

            methodFn()
                .pipe(takeUntil(this._subRelease))
                .subscribe(() => {
                    // Clear the about button as active.
                    item.Active = false;

                    // Restore focus to the previously active item.
                    curActive.Active = true;
                    const btn = this.navmenu.nativeElement.querySelector('#NavBtn_' + curActive.ButtonIndex);
                    if (btn) {
                        btn.focus();
                    }
                });
            return;
        }

        this.navigate.emit((item.ActionRoute || [''])[0]);

        // Attempt to navigate to the action route, if one is defined.
        if (item.ActionRoute) {
            this.router.navigate(item.ActionRoute);
        }
    }

    ngOnDestroy() {
        this._subRelease.next();
        this._subRelease.complete();
    }

    ngOnInit() {
        // Hook into the router event structure so we can monitor successful route changes in order to update the active
        // button (and also remove buttons which may not apply).
        this.router.events
            .pipe(
                takeUntil(this._subRelease),
                filter(event => event instanceof NavigationEnd),
                map(event => {
                    const navev: NavigationEnd = <NavigationEnd>event;
                    return navev.url;
                })
            )
            .subscribe(url => {
                this.UpdateMenuItems(url);
            });
    }

    ngOnChanges() {
        const self = this;
        const curRole = self.user && self.user.CurrentRole && self.user.CurrentRole.toLowerCase();

        // Process the menu items against the user (if available) to determine which are available.
        const fitems = (self.items || []).filter(itm => {
            return itm.Exclude !== true && (itm.AllowedRoles.includes('all') || (self.user && itm.AllowedRoles.includes(curRole)));
        });

        self.FilteredItems = fitems;

        if (self.FilteredItems && self.FilteredItems.length) {
            for (let i = 0, j = self.FilteredItems.length; i < j; ++i) {
                self.FilteredItems[i].ButtonIndex = i;
            }
        }

        // Initial update the url we are currently at.
        this.UpdateMenuItems(this.router.routerState.snapshot.url);
    }

    UpdateMenuItems(url: string) {
        // Clear all active flags
        if (this.FilteredItems && this.FilteredItems.length > 0) {
            this.FilteredItems.forEach(item => {
                item.Active = false;
            });
        }

        // Check the url against all of our menu items to look for a match.
        if (this.FilteredItems && this.FilteredItems.length > 0) {
            this.FilteredItems.forEach(item => {
                if (
                    item.LinkedStates &&
                    item.LinkedStates.find(value => {
                        return url.indexOf(value) >= 0;
                    }) != null
                ) {
                    // Found a match on this item. Set the button to being active.
                    item.Active = true;
                }
            });
        }
    }
}

export class NavMenuItem {
    ButtonIndex?: number;
    MenuText: string;
    MenuIcon?: string;
    ActionRoute?: any[];
    LinkedStates?: string[];
    AllowedRoles?: string[];
    IsAbout?: boolean;
    IsHome?: boolean;
    IsLogout?: boolean;
    Color?: string;
    Active?: boolean;
    Exclude?: boolean;
}



// WEBPACK FOOTER //
// ./src/app/ui/navmenu/navmenu.component.ts